import type { MediaFile } from '@kit/uploads';

import type {
  ApplicationFieldConfig,
  FieldOption,
} from '../../types/audition.types';

/**
 * Audition context the `age_category` field needs to compute the dancer's age
 * division from their date-of-birth answer: the competition's explicit age
 * reference date and the first event date (the Jan-1 fallback).
 */
export interface AgeCategoryContext {
  ageCutDate?: string | null;
  firstEventDate?: string | null;
  /** The form key holding the date of birth (default `birthdate`). */
  dobFieldKey?: string;
}

/**
 * Resolve the option list for a choice field. Static options declared on the
 * field win; otherwise the caller-supplied `dynamicOptions` map (keyed by
 * `field.key`) provides them. Returns `[]` when neither exists — which is the
 * bug class this guards against: a `select`/`multiselect` field with no
 * options rendered an empty dropdown (e.g. `residenceCountry`, `nationalities`).
 */
export function resolveOptions(
  field: ApplicationFieldConfig,
  dynamicOptions?: Record<string, FieldOption[]>,
): FieldOption[] {
  if (field.options?.length) return field.options;
  return dynamicOptions?.[field.key] ?? [];
}

/**
 * Builds the react-hook-form `rules` for a structured field so that
 * `validation.required` is actually ENFORCED by `form.trigger()` (the
 * Next/Submit gate in StepperFormRenderer), not just shown as a cosmetic `*`.
 * Handles every field-type's value shape: strings (text/email/date/select/
 * radio), arrays (multiselect/checkboxGroup), numbers, media objects
 * (file/video), and booleans (a required checkbox must be checked). Returns
 * undefined for non-required fields so optional fields stay untouched.
 */
export function buildRequiredRule(
  field: ApplicationFieldConfig,
  requiredMessage: string,
): { validate: (value: unknown) => true | string } | undefined {
  if (!field.validation?.required) return undefined;
  const isPresent = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (field.type === 'checkbox') return value === true;
    return true; // numbers, dates, media objects: presence === non-null
  };
  return {
    validate: (value) => (isPresent(value) ? true : requiredMessage),
  };
}

/**
 * Strip the transient in-session `preview` (an object-URL for a freshly-picked
 * image) before a MediaFile is written into the form value. The apply form
 * persists this value into `audition_applications.fields` and re-POSTs it on
 * every autosave/submit; a base64 preview would bloat the body past Vercel's
 * ~4.5 MB server-action cap → 413 (BALLEE-F2 / BAL-544).
 *
 * `signedUrl` is deliberately KEPT, not stripped. The MediaUploader re-seeds its
 * internal `files` from this persisted value after upload (the onUploadComplete
 * echo, `use-media-upload.ts:setInitialFiles`), and that reseed revokes the
 * object-URL preview — so a freshly-uploaded VIDEO (which never has a `preview`)
 * renders in-session ONLY from `signedUrl`. Removing it here blanks the tile the
 * moment it's uploaded.
 *
 * The persisted `signedUrl` DOES expire (1h), but every reload/read surface
 * re-mints a fresh one from the durable `storagePath` via `resignSavedMediaFields`
 * (`apps/web/lib/media/media-fields.ts`) — the apply wizard AND the dancer's
 * generic audition edit view both call it — so the stale persisted token is
 * never what the browser fetches on a later visit. That re-sign-on-load, not
 * stripping, is the guard against the "Failed to load video" 400.
 */
export function toPersistedMediaFile(file: MediaFile): MediaFile {
  if (file.preview === undefined) return file;
  const { preview: _preview, ...rest } = file;
  return rest;
}
